import { useIsFocused } from "expo-router/react-navigation";
import { router, useLocalSearchParams } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect, useLayoutEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useOnBoardingSecular } from '@/api/hooks/meHooks';
import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { useCreateManyPrograms } from '@/api/hooks/programHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import TotalGrantProgramForm from '@/components/forms/TotalGrantProgramForm';
import TextInputField from '@/components/inputs/TextInputField';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import {
  TotalGrantProgramData,
  TotalGrantsProgramsData,
  totalGrantsProgramsResolver
} from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';

// Just for CHARITIES type
const TotalGrantsPrograms = () => {
  const { totalGrantProgamJson, totalGrantProgamIndex } = useLocalSearchParams<{
    totalGrantProgamJson: string;
    totalGrantProgamIndex: string;
  }>();
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useOnBoardingSecular();
  const { mutateAsync: createManyPrograms, isPending: programsPending } = useCreateManyPrograms();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  const [isLoading, setIsLoading] = useState(false);

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    // Only Charities
    if (isFocused) setCurrentPage(13);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (totalGrantProgamJson) {
      const totalGrantProgramData = safeJsonParse<TotalGrantProgramData>(totalGrantProgamJson);
      if (totalGrantProgramData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !totalGrantProgamIndex && !fields.find((field) => objectComparision(field, totalGrantProgramData))
          ? append(totalGrantProgramData)
          : update(+totalGrantProgamIndex, totalGrantProgramData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalGrantProgamJson]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    formState: { isValid, submitCount, errors }
  } = useForm<TotalGrantsProgramsData>({
    defaultValues: {
      totalGrantsPrograms:
        typeof nonProfitOrg?.totalGrantsPrograms === 'number' ? nonProfitOrg?.totalGrantsPrograms : undefined
    },
    mode: 'onSubmit',
    resolver: totalGrantsProgramsResolver
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'grantsPrograms'
  });

  const onSubmit: SubmitHandler<TotalGrantsProgramsData> = async (data) => {
    setIsLoading(true);
    const mappedData = data.grantsPrograms.map((program) => ({
      ...program,
      nonProfitOrgId: authState.signUpData?.loginEmail?.npoId ?? ''
    }));
    const body = {
      programs: mappedData
    };

    try {
      await nonProfitUpdate({
        npoId: authState.signUpData?.loginEmail?.npoId ?? '',
        totalGrantsPrograms: Number(data.totalGrantsPrograms)
      });
      await createManyPrograms(body);
      await onBoardingUpdate({
        preferenceId: authState.preferenceId ?? '',
        programs: true
      });

      setIsLoading(false);
      router.navigate('/(app)/login/nonProfit/onBoarding/lightRatingQuestions');
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <View style={styles.totalGrantProgramsSectionContainer}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.total_grants_programs.title')}
            description={i18n.t('sign_in.nonProfit.total_grants_programs.description')}
            infoButtonLabel={i18n.t('sign_in.nonProfit.total_grants_programs.info')}
          />

          <TextInputField
            name="totalGrantsPrograms"
            control={control}
            placeholder={i18n.t('sign_in.nonProfit.total_grants_programs.totalGrantsPrograms_placeholder')}
            type="numeric"
            currency
            returnKeyLabel="next"
            returnKeyType="next"
          />
        </View>
        <Divider style={styles.sectionDivider} />

        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.total_grants_programs.title_2')}
          description={i18n.t('sign_in.nonProfit.total_grants_programs.description_2')}>
          {getValues('grantsPrograms') && getValues('grantsPrograms').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 200 - top - bottom }}>
              <TotalGrantProgramForm
                initialData={getValues('grantsPrograms.0')}
                showDoneButton
                showBackButton
                onDone={(data) => {
                  insert(0, data);
                  trigger('grantsPrograms.0').then(() => {
                    trigger().then((isArrayValid) => {
                      if (isArrayValid) {
                        onSubmit(getValues());
                      }
                    });
                  });
                }}
                onAdd={(data) => {
                  update(0, data);
                  trigger('grantsPrograms.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/login/nonProfit/addUpdateTotalGrantProgram');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.next')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(grantProgram) => grantProgram.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/login/nonProfit/addUpdateTotalGrantProgram',
                        params: {
                          totalGrantProgamJson: JSON.stringify(field.item),
                          totalGrantProgamIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.name}
                    subtitle={field.item.description ?? '-'}
                    budget={field.item.budget}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/login/nonProfit/addUpdateTotalGrantProgram')}
                    label={i18n.t('sign_in.nonProfit.total_grants_programs.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        {errors.grantsPrograms && <Text style={styles.errorText}>{errors?.grantsPrograms?.message}</Text>}

        <View style={styles.buttonsContainer}>
          {getValues('grantsPrograms') && getValues('grantsPrograms').length > 0 && (
            <View style={styles.nextBackContainer}>
              {router.canGoBack() && (
                <BrandedButton
                  disabled={onBoardingIsPending || programsPending || nonProfitIsPending || isLoading}
                  style={styles.flexOne}
                  onPress={router.back}>
                  {capitalize(i18n.t('mix.back'))}
                </BrandedButton>
              )}
              <BrandedButton
                style={styles.flexOne}
                loading={onBoardingIsPending || programsPending || nonProfitIsPending || isLoading}
                disabled={!isValid && submitCount > 0}
                onPress={handleSubmit(onSubmit)}>
                {i18n.t('mix.next')}
              </BrandedButton>
            </View>
          )}
          <SaveAndContinueLaterButton
            disabled={onBoardingIsPending || programsPending || nonProfitIsPending || isLoading}
          />
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  flexOne: {
    flex: 1
  },

  totalGrantProgramsSectionContainer: {
    flexDirection: 'column',
    gap: 15
  },

  sectionDivider: {
    marginVertical: 25
  },

  nextBackContainer: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column'
  },

  errorText: {
    color: 'red',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10
  },

  divider: {
    marginBottom: 15
  }
});

export default TotalGrantsPrograms;
